




























































import Vue from 'vue'
import Swiper, {
  Autoplay,
  Navigation,
  Pagination,
  Scrollbar,
  Virtual,
  FreeMode
} from 'swiper'

export default Vue.extend({
  props: {
    /**
     * Unique key to initialize and identify the carousel instance
     * Used to allow multiple carousels on the same page
     * and to prevent carousels from interfering with each other
     */
    carouselKey: {
      type: String,
      required: true
    },
    /**
     * Array of items to be displayed
     * Each item is passed to the default slot
     * and can be accessed via the `item` prop in the default slot's scope
     */
    items: {
      type: Array as () => any[],
      required: true
    },
    /**
     * Number of slides to display at once
     * If not specified, defaults to 1
     */
    slidesPerView: {
      type: Number,
      default: 1
    },
    /**
     * Number of slides to move at once
     * If not specified, defaults to the value of `slidesPerView`
     * If specified, must be > 0 and <= to the value of `slidesPerView`
     */
    slidesPerGroup: {
      type: Number,
      default: 0
    },
    /**
     * Space between slides, in pixels (px)
     * If not specified, defaults to 0
     */
    spaceBetweenSlides: {
      type: Number,
      default: 0
    },
    /**
     * Duration of transition between slides (ms)
     * If not specified, defaults to 300
     */
    speed: {
      type: Number,
      default: 300
    },
    /**
     * Whether or not to rewind to the first slide
     * after reaching the last slide and vice versa
     * If not specified, defaults to false
     */
    rewind: {
      type: Boolean,
      default: false
    },
    /**
     * Delay between slides, in milliseconds (ms)
     * If not specified, defaults to 0 (no autoplay)
     */
    autoPlayDelay: {
      type: Number,
      default: 0
    },
    /**
     * Whether or not to make active slide be at the center,
     * not always on the left side
     */
    centeredSlides: {
      type: Boolean,
      default: false
    },
    /**
     * Defines how many slides before end/beginning it should rearrange (loop) slides.
     * If specified and > 0, enables loop mode
     * If not specified, defaults to slidesPerView
     * NOTE: only use this or `rewind` at a time
     */
    loopedSlides: {
      type: Number || null,
      default: null
    },
    /**
     * Whether or not to show pagination bullets below slides
     * If not specified, defaults to false
     * NOTE: only one of `showPagination` and `showScrollbar` can be used at a time
     */
    showPagination: {
      type: Boolean,
      default: false
    },
    /**
     * Whether or not to show horizontal scrollbar below slides
     * If not specified, defaults to false
     * NOTE: only one of `showPagination` and `showScrollbar` can be used at a time
     */
    showScrollbar: {
      type: Boolean,
      default: false
    },
    /**
     * Whether or not to enable free mode
     * If not specified, defaults to false
     */
    freeMode: {
      type: Boolean,
      default: false
    },
    /**
     * Whether or not to enable virtual slides
     * If not specified, defaults to true
     */
    enableVirtual: {
      type: Boolean,
      default: true
    },
    /**
     * If you want to use your own written custom previous button
     * Carousel will use the class name to assign functionality of the button
     */
    customPrevButtonClass: {
      type: String,
      default: ''
    },
    /**
     * If you want to use your own written custom next button
     * Carousel will use the class name to assign functionality of the button
     */
    customNextButtonClass: {
      type: String,
      default: ''
    },
    /**
     * Number of next and previous slides to preload
     * Only applicable if using lazy loading
     * If not specified, defaults to 0
     */
    lazyPreloadPrevNext: {
      type: Number,
      default: 0
    }
  },
  data(): {
    swiper: any
  } {
    return {
      swiper: undefined
    }
  },
  mounted() {
    this.swiper = new Swiper(`.${this.carouselKey}-Carousel_Container`, {
      modules: [Autoplay, Navigation, Pagination, Scrollbar, Virtual, FreeMode],
      slidesPerView: this.slidesPerView,
      slidesPerGroup: this.slidesPerGroup > 0
        ? this.slidesPerGroup <= this.slidesPerView
          ? this.slidesPerGroup
          : this.slidesPerView
        : this.slidesPerView,
      spaceBetween: this.spaceBetweenSlides,
      speed: this.speed,
      centeredSlides: this.centeredSlides,
      loop: this.loopedSlides > 0,
      loopedSlides: this.loopedSlides,
      rewind: this.rewind && this.loopedSlides === null,
      autoplay: this.autoPlayDelay > 0
        ? {
            delay: 5000,
            disableOnInteraction: true
          }
        : false,
      navigation: {
        prevEl: `.${this.customPrevButtonClass || `${this.carouselKey}-Carousel_ButtonPrev`}`,
        nextEl: `.${this.customNextButtonClass || `${this.carouselKey}-Carousel_ButtonNext`}`
      },
      pagination: this.showPagination && !this.showScrollbar
        ? {
            el: `.${this.carouselKey}-Carousel_Pagination`,
            clickable: true
          }
        : false,
      scrollbar: this.showScrollbar && !this.showPagination
        ? {
            el: `.${this.carouselKey}-Carousel_Scrollbar`,
            draggable: true,
            hide: false
          }
        : false,
      freeMode: this.freeMode,
      virtual: {
        enabled: this.enableVirtual,
        addSlidesAfter: this.freeMode
          ? Math.ceil(this.slidesPerView) + 1
          : 0,
        addSlidesBefore: this.freeMode
          ? Math.ceil(this.slidesPerView) + 1
          : 0
      },
      lazyPreloadPrevNext: this.lazyPreloadPrevNext
    })
  }
})
